/**
 * NotFoundPage — a genuine 404 page.
 *
 * None existed before this gate (existing catch-alls only `Navigate`-redirect).
 * This is the destination for routes OMITTED by `buildGatedRoutes` (a disabled
 * release toggle) and for genuinely unknown URLs in all three SPAs
 * (SPEC-FEATURE-TOGGLES-RULE-005, SCENARIO-003).
 *
 * All user-facing strings use Lingui macros and ship Hungarian entries in
 * web/src/i18n/locales/hu/messages.po (SPEC-FEATURE-TOGGLES-RULE-011).
 *
 * @module feature-gate/NotFoundPage
 * @see SPEC-FEATURE-TOGGLES
 */

import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { Trans } from "@lingui/react/macro";

export interface NotFoundPageProps {
  /** Where the back-link points. Defaults to the SPA root. */
  homePath?: string;
}

/**
 * Full-page 404 with a heading, explanatory body, and a back-to-home link.
 */
export const NotFoundPage: React.FC<NotFoundPageProps> = ({
  homePath = "/",
}) => {
  return (
    <Box
      role="alert"
      aria-live="polite"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      gap={2}
      px={3}
      py={8}
      minHeight="60vh"
    >
      <Typography variant="h1" component="p" color="text.secondary" sx={{ fontWeight: 700 }}>
        404
      </Typography>
      <Typography variant="h5" component="h1">
        <Trans>Page not found</Trans>
      </Typography>
      <Typography variant="body1" color="text.secondary" maxWidth={480}>
        <Trans>
          The page you are looking for does not exist or is not available in this deployment.
        </Trans>
      </Typography>
      <Button component={RouterLink} to={homePath} variant="contained" sx={{ mt: 2 }}>
        <Trans>Back to home</Trans>
      </Button>
    </Box>
  );
};

export default NotFoundPage;
